import {Box, Typography, useTheme} from '@mui/material'
import {scaleBand, scaleLinear} from 'd3'
import type {FC, ReactElement} from 'react'
import {memo, useMemo, useRef, useState} from 'react'
import {nivoDarkColors} from '@shared/components/graphThemes/nivoDarkColors'
import {nivoLightColors} from '@shared/components/graphThemes/nivoLightColors'
import ChartTooltip from './ChartTooltip'
import ChartActionsMenu from './ChartActionsMenu'
import {CHART_VIEW_H as VIEW_H, CHART_VIEW_W as VIEW_W, sanitizeChartColor} from './chartConstants'
import {resizableGraphPanelSx} from './chartStyles'
import {useResizeGrip} from '@containers/Dashboards/AgentChat/components/messages/useResizeGrip'
import type {BarChartSpec, BarSeries} from './types'

/**
 * Categorical bar chart (native d3). Supports vertical/horizontal orientation,
 * single or multiple series, and grouped (side-by-side) or stacked layouts.
 * Single series colours by category; multiple series colour by series + legend.
 */

interface Props {
  spec: BarChartSpec
}

interface Bar {
  key: string
  x: number
  y: number
  w: number
  h: number
  color: string
  seriesName: string
  label: string
  value: number
}

const ChatBarChart: FC<Props> = ({spec}): ReactElement | null => {
  const theme = useTheme()
  const palette = theme.palette.mode === 'dark' ? nivoDarkColors : nivoLightColors
  const axisColor = theme.palette.text.secondary
  const gridColor = theme.palette.divider

  const panelRef = useRef<HTMLDivElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [hover, setHover] = useState<{bar: Bar; left: number; top: number} | null>(null)
  useResizeGrip(panelRef, {mode: 'aspect', aspectRatio: VIEW_W / VIEW_H})

  const horizontal = spec.orientation === 'horizontal'
  const stacked = !!spec.stacked

  const model = useMemo(() => {
    const series: BarSeries[] = spec.series?.length
      ? spec.series
      : [{name: '', data: spec.data ?? []}]
    const multi = series.length > 1

    const categories: string[] = []
    for (const s of series) {
      for (const d of s.data) if (!categories.includes(d.label)) categories.push(d.label)
    }
    const valueOf = (s: BarSeries, label: string): number =>
      s.data.find((d) => d.label === label)?.value ?? 0

    const margin = horizontal
      ? {top: 14, right: 24, bottom: 28, left: 120}
      : {top: 16, right: 18, bottom: 40, left: 52}
    const innerW = VIEW_W - margin.left - margin.right
    const innerH = VIEW_H - margin.top - margin.bottom

    const allValues = series.flatMap((s) => s.data.map((d) => d.value))
    const stackedMax = Math.max(
      0,
      ...categories.map((c) => series.reduce((sum, s) => sum + Math.max(0, valueOf(s, c)), 0)),
    )
    const vMax = stacked ? stackedMax : Math.max(0, ...allValues)
    const vMin = Math.min(0, ...allValues)

    const band = scaleBand<string>()
      .domain(categories)
      .range([0, horizontal ? innerH : innerW])
      .padding(0.2)
    const value = scaleLinear()
      .domain([vMin, vMax])
      .nice()
      .range(horizontal ? [0, innerW] : [innerH, 0])
    const inner =
      multi && !stacked
        ? scaleBand<string>()
            .domain(series.map((s) => s.name))
            .range([0, band.bandwidth()])
            .padding(0.1)
        : null
    const base = value(0)

    const bars: Bar[] = []
    categories.forEach((c) => {
      let cum = 0 // running total in value units, for stacking
      series.forEach((s, i) => {
        const v = valueOf(s, c)
        // Explicit colours win: a per-bar colour on the datum (e.g. a classification's
        // system colour), then a whole-series colour, then the positional palette.
        const color =
          sanitizeChartColor(s.data.find((d) => d.label === c)?.color) ??
          sanitizeChartColor(s.color) ??
          (multi ? palette[i % palette.length] : palette[categories.indexOf(c) % palette.length])
        const bandPos = band(c) ?? 0
        const bw = band.bandwidth()
        let x: number
        let y: number
        let w: number
        let h: number
        if (stacked) {
          const a = value(cum)
          const b = value(cum + v)
          cum += v
          if (horizontal) {
            x = Math.min(a, b)
            w = Math.abs(b - a)
            y = bandPos
            h = bw
          } else {
            y = Math.min(a, b)
            h = Math.abs(b - a)
            x = bandPos
            w = bw
          }
        } else {
          const a = base
          const b = value(v)
          const off = inner ? (inner(s.name) ?? 0) : 0
          const segW = inner ? inner.bandwidth() : bw
          if (horizontal) {
            x = Math.min(a, b)
            w = Math.abs(b - a)
            y = bandPos + off
            h = segW
          } else {
            y = Math.min(a, b)
            h = Math.abs(b - a)
            x = bandPos + off
            w = segW
          }
        }
        bars.push({
          key: `${c}__${s.name}__${i}`,
          x,
          y,
          w,
          h,
          color,
          seriesName: s.name,
          label: c,
          value: v,
        })
      })
    })

    const fmt = value.tickFormat(5)
    const valueTicks = value.ticks(5).map((t) => ({v: t, pos: value(t), label: fmt(t)}))
    const step = Math.ceil(categories.length / (horizontal ? 20 : 12))
    const bandLabels = categories
      .filter((_, i) => i % step === 0)
      .map((c) => ({label: c, pos: (band(c) ?? 0) + band.bandwidth() / 2}))
    const legend = multi
      ? series.map((s, i) => ({
          name: s.name,
          color: sanitizeChartColor(s.color) ?? palette[i % palette.length],
        }))
      : []

    return {bars, valueTicks, bandLabels, legend, margin, innerW, innerH, base}
  }, [spec, palette, horizontal, stacked])

  if (!model.bars.length) return null
  const {margin, innerW, innerH} = model

  const onMove = (e: React.MouseEvent<Element>, bar: Bar): void => {
    const el = wrapRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setHover({bar, left: e.clientX - rect.left, top: e.clientY - rect.top})
  }

  return (
    <Box ref={panelRef} sx={resizableGraphPanelSx}>
      <ChartActionsMenu
        svgRef={svgRef}
        title={spec.title}
        legend={model.legend.map((l) => ({label: l.name, color: l.color}))}
      />
      {spec.title ? (
        <Typography variant="subtitle2" sx={{mb: 0.5, fontWeight: 600}}>
          {spec.title}
        </Typography>
      ) : null}

      {model.legend.length ? (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0.5}}>
          {model.legend.map((l) => (
            <Box key={l.name} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <Box sx={{width: 10, height: 10, borderRadius: '2px', bgcolor: l.color}} />
              <Typography variant="caption" sx={{color: 'text.secondary'}}>
                {l.name}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      <Box
        ref={wrapRef}
        data-graph-area
        onMouseLeave={() => setHover(null)}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${VIEW_W} / ${VIEW_H}`,
          overflow: 'visible',
        }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={spec.title ?? 'bar chart'}
          style={{width: '100%', height: '100%', display: 'block', overflow: 'visible'}}
        >
          <g transform={`translate(${margin.left},${margin.top})`}>
            {model.valueTicks.map((t) =>
              horizontal ? (
                <g key={`v-${t.v}`} transform={`translate(${t.pos},0)`}>
                  <line y1={0} y2={innerH} stroke={gridColor} strokeWidth={1} opacity={0.5} />
                  <text x={0} y={innerH + 16} textAnchor="middle" fontSize={11} fill={axisColor}>
                    {t.label}
                  </text>
                </g>
              ) : (
                <g key={`v-${t.v}`} transform={`translate(0,${t.pos})`}>
                  <line x1={0} x2={innerW} stroke={gridColor} strokeWidth={1} opacity={0.5} />
                  <text x={-8} dy="0.32em" textAnchor="end" fontSize={11} fill={axisColor}>
                    {t.label}
                  </text>
                </g>
              ),
            )}

            {model.bandLabels.map((b) =>
              horizontal ? (
                <text
                  key={`b-${b.label}`}
                  x={-8}
                  y={b.pos}
                  dy="0.32em"
                  textAnchor="end"
                  fontSize={11}
                  fill={axisColor}
                >
                  {b.label.length > 18 ? `${b.label.slice(0, 17)}…` : b.label}
                </text>
              ) : (
                <text
                  key={`b-${b.label}`}
                  x={b.pos}
                  y={innerH + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fill={axisColor}
                >
                  {b.label.length > 10 ? `${b.label.slice(0, 9)}…` : b.label}
                </text>
              ),
            )}

            {model.bars.map((b) => (
              <rect
                key={b.key}
                x={b.x}
                y={b.y}
                width={Math.max(0, b.w)}
                height={Math.max(0, b.h)}
                rx={2}
                fill={b.color}
                opacity={hover && hover.bar.key !== b.key ? 0.55 : 1}
                onMouseMove={(e) => onMove(e, b)}
              />
            ))}

            {horizontal ? (
              <line
                x1={model.base}
                x2={model.base}
                y1={0}
                y2={innerH}
                stroke={axisColor}
                strokeWidth={1}
              />
            ) : (
              <line
                x1={0}
                x2={innerW}
                y1={model.base}
                y2={model.base}
                stroke={axisColor}
                strokeWidth={1}
              />
            )}
          </g>
        </svg>

        <ChartTooltip left={hover?.left ?? 0} top={hover?.top ?? 0} visible={Boolean(hover)}>
          {hover ? (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <Box sx={{width: 8, height: 8, borderRadius: '2px', bgcolor: hover.bar.color}} />
              <Typography variant="caption" sx={{color: 'text.secondary'}}>
                {hover.bar.seriesName ? `${hover.bar.seriesName} · ` : ''}
                {hover.bar.label}: <strong>{hover.bar.value}</strong>
              </Typography>
            </Box>
          ) : null}
        </ChartTooltip>
      </Box>
    </Box>
  )
}

// Memoised (stable spec identity) so streaming re-renders skip this chart.
export default memo(ChatBarChart)
